import React from 'react'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { GetServerSideProps } from 'next'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import Image from 'next/image'
import {
  getStakingPlatforms,
  getEthStats,
  getStakingPlatformTVLS,
} from '../src/services/stakingPlatforms'
import { StakingPlatform, EthStats, StakingPlatformTVL } from '../types/stakingPlatform'
import EthStakingTable from '../src/components/Product/earn/eth-staking/EthStakingTable'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import FAQSection from '../src/components/Product/FAQSection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'
import LearnMore from '../src/components/Product/LearnMore'
import EthStakingStats from '../src/components/Product/earn/eth-staking/EthStakingStats'

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  container: {
    margin: '0',
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 0',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
}))

const faqs = [
  {
    question: 'What are staking pools?',
    answer:
      'Staking pools allow users to participate in the validating process of Ethereum and earn rewards without having to deal with the technical complexity of running nodes and the capital requirements (32 ETH per node).',
  },
  {
    question: 'What is the interest rate for staking Ethereum?',
    answer: `The rewards paid for staking are determined algorithmically by the Ethereum network. The more people stake the lower the rewards. For example, at 1m ETH staked the annual interest rate is 15.7%, at 2m ETH it's 11% and at 3m it’s 9%. You can track the amount of ETH staked on [DuneAnalytics](https://www.duneanalytics.com/hagaetc/eth2-0-deposits).`,
  },
  {
    question: 'What is the difference between a “centralized” and “DeFi” staking pool?',
    answer:
      'Centralized staking pools take control of your Ether and stake it for you. Decentralized staking pools give you more control over your funds and also give you a deposit token (e.g $sETH2) that you can use in the DeFi ecosystem to take a loan for instance or do other yield generating activities. For these reasons it’s financially more advantageous to stake with a DeFi staking pool.',
  },
  {
    question: 'Can I exit my stake anytime?',
    answer: `The deposits and rewards are locked on the Ethereum 2.0 chain until “The Merge” (early 2022 according to roadmap) which will merge the two Blockchains together and finalize Ethereum’s transition to [Proof-of-Stake](https://cryptotesters.com/blog/ethereum-staking-explained). However, you can sell your deposit token earlier - albeit at a small discount.`,
  },
]

interface props {
  stakingPlatforms: StakingPlatform[]
  ethStats: EthStats
  stakingPlatformsTVLS: StakingPlatformTVL[]
}

const EthStaking = ({ stakingPlatforms, ethStats, stakingPlatformsTVLS }: props): JSX.Element => {
  const classes = useStyles({})

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`The Best Ethereum Staking Platforms`}
            description={`Earn yield on your Ether by depositing it on an Ethereum staking platform`}
            openGraph={{
              url: `https://cryptotesters.com${urls.ethereumStaking}`,
              title: `The Best Ethereum Staking Platforms`,
              description: `Earn yield on your Ether by depositing it on an Ethereum staking platform`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/sharing_powered_by_cryptostats.jpeg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/sharing_powered_by_cryptostats.jpeg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">
                    Best Ethereum <span className={classes.purpleText}> staking platforms. </span>{' '}
                  </Typography>
                  <Typography variant="body2">
                    Ethereum staking platforms let you participate in the transaction validation
                    process of the Ethereum network - called Proof-of-Stake. When you stake your
                    Ether (ETH) you earn rewards paid in ETH.
                  </Typography>
                  <Button
                    color="primary"
                    variant="contained"
                    href="#staking-table"
                    className={classes.heroCTA}
                  >
                    Compare staking platforms
                  </Button>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <Image src="/earn/eth-staking.png" objectFit="contain" layout="fill" />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
            <div className={`${classes.section} ${classes.container}`}>
              <EthStakingStats
                stakedEth={ethStats.stakedEth}
                APY={ethStats.APY}
                stakedEthDollars={ethStats.stakedEthDollars}
              />
            </div>

            <div id="staking-table">
              <EthStakingTable
                platforms={stakingPlatforms}
                title="Compare Ethereum staking platforms"
                tvls={stakingPlatformsTVLS}
              />
            </div>
            <div>
              <FAQSection faqs={faqs} />
              <LearnMore
                header="Learn more about staking."
                title="Staking explained!"
                content="If you’re a crypto-trading beginner, look for a platform with a simple and straightforward interface. A good exchange starts simple, and hides complex features for advanced traders in the settings."
                cta={{
                  label: 'See staking platforms ->',
                  url: urls.ethereumStaking,
                }}
                video="https://www.youtube.com/embed/bLlUH91NAA0"
              />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let stakingPlatforms: StakingPlatform[] = []
  let ethStats: EthStats = { stakedEth: 0, APY: 0, stakedEthDollars: 0 }
  let stakingPlatformsTVLS: StakingPlatformTVL[] = []

  try {
    stakingPlatforms = await getStakingPlatforms()
    ethStats = await getEthStats()
    stakingPlatformsTVLS = await getStakingPlatformTVLS()
  } catch (e) {
    console.error(e)
  }

  return {
    props: {
      stakingPlatforms,
      ethStats,
      stakingPlatformsTVLS,
    },
  }
}

export default EthStaking
